import { create } from 'zustand'
import type { FiltersState } from '~/client/common/filters'
import { countAppliedFilters, createEmptyFiltersState } from '~/client/common/filters'
import { DEFAULT_SPACE_SORT_BY, type SpaceSortBy } from '~/shared/space/space-list.params'

// ── Types ────────────────────────────────────────────────────────────────────

export interface LatLng {
  lat: number
  lng: number
}

export interface MapBounds {
  north: number
  south: number
  east: number
  west: number
}

/** Viewport payload emitted by the map: geohash corners + raw bounds. */
export interface BoundsPayload {
  nwGeohash: string
  seGeohash: string
  bounds: MapBounds
}

// ── Store state ──────────────────────────────────────────────────────────────

interface ListingsState {
  // Filter state (single source of truth — no draft/applied split)
  filters: FiltersState | null
  filtersKey: number
  lastAppliedAt: number

  // City / venue-group context (set during hydration, avoids auth-store race)
  activeVenueGroupId: number | undefined

  // Map state
  searchAsMapMoves: boolean
  mapCenter: LatLng | null
  lastBounds: MapBounds | null

  // UI state
  filtersHidden: boolean
  currentPage: number
  sortBy: SpaceSortBy

  // Marker / scroll interaction
  highlightSpaceId: number | null
  hoveredSpaceId: number | null
  scrollToIndex: number | null

  // Page items for map pins (set by results panel after fetch)
  pageItems: any[]
  pageStartIndex: number
  suggestedSpaces: any[]
  visibleRange: { start: number; end: number } | null

  // Ghost venue creation tracking
  isCreatingGhostVenue: boolean

  // External reset trigger (e.g. logo click from AppHeader)
  resetTrigger: number

  // Event list dialog
  spaceBeingAddedToEvent: { spaceId: number; spaceName: string } | null

  // Auto-filter notification banner (shown after event creation from homepage)
  autoFilterBanner: { count: number; eventName: string } | null

  // Secondary map spaces (lightweight pins beyond current page)
  secondaryMapSpaces: any[]
  // Previewed space from secondary pin click (full data fetched on demand)
  previewedSpace: any | null

  // Current map zoom level (tracked for zoom-based pin rendering)
  mapZoom: number | null
  // Whether secondary map spaces are currently loading
  secondaryMapLoading: boolean
}

interface ListingsActions {
  // Filter actions
  setFilters: (filters: FiltersState | null) => void
  updateFilter: <K extends keyof FiltersState>(key: K, value: FiltersState[K]) => void
  resetFilters: () => void
  bumpFiltersKey: () => void

  // Map actions
  setSearchAsMapMoves: (v: boolean) => void
  onBoundsChange: (payload: BoundsPayload) => void
  /** Apply the given viewport as the active search area regardless of the
   * `searchAsMapMoves` flag. The manual one-shot path — never dedups. */
  applyBoundsAsFilter: (payload: BoundsPayload) => void
  setMapCenter: (v: LatLng | null) => void
  setLastBounds: (v: MapBounds | null) => void

  // UI actions
  toggleFiltersHidden: () => void
  setPage: (page: number) => void
  setSortBy: (sortBy: SpaceSortBy) => void

  // Marker / scroll actions
  onMarkerClick: (payload: { spaceId: number; index: number }) => void
  onSuggestedMarkerClick: (payload: { spaceId: number; index: number }) => void
  setHighlightSpaceId: (id: number | null) => void
  setHoveredSpaceId: (id: number | null) => void
  setScrollToIndex: (index: number | null) => void

  // Page items for map (set by results after fetch)
  setPageItems: (items: any[]) => void
  setPageStartIndex: (index: number) => void
  setSuggestedSpaces: (spaces: any[]) => void
  setVisibleRange: (range: { start: number; end: number } | null) => void

  // Ghost venue
  setIsCreatingGhostVenue: (v: boolean) => void

  // Event list dialog
  setSpaceBeingAddedToEvent: (v: { spaceId: number; spaceName: string } | null) => void

  // Hydrate from URL on mount
  hydrate: (filters: FiltersState | null, page: number, activeVenueGroupId?: number, sortBy?: SpaceSortBy) => void

  // Label mappings (set by SpaceFilters2 when dynamic options load)
  setLabelMappings: (mappings: Partial<Pick<FiltersState, 'labelToCuisineId' | 'labelToVenueTypeId' | 'labelToNeighborhoodId'>>) => void

  // Auto-filter banner
  setAutoFilterBanner: (banner: { count: number; eventName: string } | null) => void

  // Secondary map spaces
  setSecondaryMapSpaces: (spaces: any[]) => void
  setPreviewedSpace: (space: any | null) => void
  onSecondaryMarkerClick: (payload: { spaceId: number }) => void

  // Map zoom
  setMapZoom: (zoom: number | null) => void
  setSecondaryMapLoading: (loading: boolean) => void
}

export type ListingsStore = ListingsState & ListingsActions

const hasDistanceSortOrigin = (filters: FiltersState | null | undefined): boolean =>
  typeof filters?.locationLat === 'number' && typeof filters.locationLng === 'number'

const coerceSortByForFilters = (sortBy: SpaceSortBy, filters: FiltersState | null | undefined): SpaceSortBy =>
  sortBy === 'distance' && !hasDistanceSortOrigin(filters) ? DEFAULT_SPACE_SORT_BY : sortBy

// ── Selectors (for granular subscriptions) ───────────────────────────────────

export const selectFilters = (s: ListingsStore) => s.filters
export const selectFiltersKey = (s: ListingsStore) => s.filtersKey
export const selectLastAppliedAt = (s: ListingsStore) => s.lastAppliedAt
export const selectCurrentPage = (s: ListingsStore) => s.currentPage
export const selectSortBy = (s: ListingsStore) => s.sortBy
export const selectFiltersHidden = (s: ListingsStore) => s.filtersHidden
export const selectSearchAsMapMoves = (s: ListingsStore) => s.searchAsMapMoves
export const selectMapCenter = (s: ListingsStore) => s.mapCenter
export const selectHighlightSpaceId = (s: ListingsStore) => s.highlightSpaceId
export const selectHoveredSpaceId = (s: ListingsStore) => s.hoveredSpaceId
export const selectScrollToIndex = (s: ListingsStore) => s.scrollToIndex
export const selectPageItems = (s: ListingsStore) => s.pageItems
export const selectPageStartIndex = (s: ListingsStore) => s.pageStartIndex
export const selectSuggestedSpaces = (s: ListingsStore) => s.suggestedSpaces
export const selectVisibleRange = (s: ListingsStore) => s.visibleRange
export const selectResetTrigger = (s: ListingsStore) => s.resetTrigger
export const selectIsCreatingGhostVenue = (s: ListingsStore) => s.isCreatingGhostVenue
export const selectSpaceBeingAddedToEvent = (s: ListingsStore) => s.spaceBeingAddedToEvent

export const selectActiveVenueGroupId = (s: ListingsStore) => s.activeVenueGroupId
export const selectAppliedFiltersCount = (s: ListingsStore) => countAppliedFilters(s.filters)
export const selectAutoFilterBanner = (s: ListingsStore) => s.autoFilterBanner

export const selectMapSpaces = (s: ListingsStore): any[] => s.pageItems
export const selectSecondaryMapSpaces = (s: ListingsStore) => s.secondaryMapSpaces
export const selectPreviewedSpace = (s: ListingsStore) => s.previewedSpace
export const selectMapZoom = (s: ListingsStore) => s.mapZoom
export const selectSecondaryMapLoading = (s: ListingsStore) => s.secondaryMapLoading

// ── Store ────────────────────────────────────────────────────────────────────

export const useListingsStore = create<ListingsStore>((set, get) => ({
  // ── Initial state ────────────────────────────────────────────────────────
  filters: null,
  filtersKey: 0,
  lastAppliedAt: 0,
  activeVenueGroupId: undefined,
  searchAsMapMoves: false,
  mapCenter: null,
  lastBounds: null,
  filtersHidden: false,
  currentPage: 1,
  sortBy: DEFAULT_SPACE_SORT_BY,
  highlightSpaceId: null,
  hoveredSpaceId: null,
  scrollToIndex: null,
  pageItems: [],
  pageStartIndex: 0,
  suggestedSpaces: [],
  visibleRange: null,
  isCreatingGhostVenue: false,
  resetTrigger: 0,
  spaceBeingAddedToEvent: null,
  autoFilterBanner: null,
  secondaryMapSpaces: [],
  previewedSpace: null,
  mapZoom: null,
  secondaryMapLoading: false,

  // ── Filter actions ───────────────────────────────────────────────────────

  setFilters: (filters) => {
    const prev = get().filters
    const sortBy = coerceSortByForFilters(get().sortBy, filters)
    // SpaceFilters fires onChange on mount when it syncs label-to-id
    // mappings.  Detect that case so we don't dismiss the auto-filter
    // banner for a non-user-initiated change.
    const labelKeys = new Set(['labelToCuisineId', 'labelToVenueTypeId', 'labelToNeighborhoodId', 'venueTagToVenueIds', 'venueTagLabelById'])
    const onlyLabelsChanged = prev != null && filters != null && (() => {
      for (const key of Object.keys(filters) as (keyof FiltersState)[]) {
        if (labelKeys.has(key)) continue
        if (prev[key] !== filters[key]) return false
      }
      return true
    })()
    set({
      filters,
      sortBy,
      lastAppliedAt: Date.now(),
      scrollToIndex: null,
      highlightSpaceId: null,
      ...(onlyLabelsChanged ? {} : { autoFilterBanner: null, currentPage: 1 }),
    })
  },

  updateFilter: (key, value) => {
    const { filters } = get()
    const base = filters ?? createEmptyFiltersState()
    const nextFilters = { ...base, [key]: value }
    set({
      filters: nextFilters,
      sortBy: coerceSortByForFilters(get().sortBy, nextFilters),
      lastAppliedAt: Date.now(),
      scrollToIndex: null,
      highlightSpaceId: null,
      autoFilterBanner: null,
    })
  },

  resetFilters: () => {
    const state = get()
    // Skip if already in reset state to avoid unnecessary renders
    if (state.filters === null && !state.searchAsMapMoves && !state.autoFilterBanner && state.sortBy !== 'distance') return
    set({
      filters: null,
      filtersKey: state.filtersKey + 1,
      lastAppliedAt: Date.now(),
      currentPage: 1,
      searchAsMapMoves: false,
      mapCenter: null,
      lastBounds: null,
      hoveredSpaceId: null,
      scrollToIndex: null,
      highlightSpaceId: null,
      autoFilterBanner: null,
      activeVenueGroupId: state.activeVenueGroupId,
      secondaryMapSpaces: [],
      previewedSpace: null,
      ...(state.sortBy === 'distance' ? { sortBy: DEFAULT_SPACE_SORT_BY } : {}),
    })
  },

  bumpFiltersKey: () => set({ filtersKey: get().filtersKey + 1 }),

  // ── Map actions ──────────────────────────────────────────────────────────

  setSearchAsMapMoves: (v) => {
    if (!v) {
      const { filters } = get()
      const next = filters ? { ...filters, boundsNwGeohash: undefined, boundsSeGeohash: undefined } : null
      set({
        searchAsMapMoves: false,
        mapCenter: null,
        filters: next,
        lastAppliedAt: Date.now(),
        currentPage: 1,
        previewedSpace: null,
      })
    } else {
      set({ searchAsMapMoves: true })
    }
  },

  onBoundsChange: (payload) => {
    if (!get().searchAsMapMoves) {
      set({ lastBounds: payload.bounds })
      return
    }
    // Skip the re-search when the viewport geohash is unchanged — debounced
    // auto-emits during a single drag often land on the same cell.
    const { filters } = get()
    const prevNw = filters?.boundsNwGeohash
    const prevSe = filters?.boundsSeGeohash
    const nextNw = payload.nwGeohash || undefined
    const nextSe = payload.seGeohash || undefined
    if (prevNw === nextNw && prevSe === nextSe) {
      set({ lastBounds: payload.bounds })
      return
    }
    get().applyBoundsAsFilter(payload)
  },

  applyBoundsAsFilter: ({ nwGeohash, seGeohash, bounds }) => {
    const { filters } = get()

    const nextNw = nwGeohash || undefined
    const nextSe = seGeohash || undefined

    const lat = (bounds.north + bounds.south) / 2
    const lng = (bounds.east + bounds.west) / 2
    const center = Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null

    const next: FiltersState = {
      ...(filters ?? createEmptyFiltersState()),
      boundsNwGeohash: nextNw,
      boundsSeGeohash: nextSe,
      location: '',
      locationPlaceLabel: undefined,
      locationLat: undefined,
      locationLng: undefined,
      radiusMiles: undefined,
    }

    // Batch lastBounds + filters into a single set() to avoid double re-renders
    set({
      lastBounds: bounds,
      mapCenter: center,
      filters: next,
      sortBy: coerceSortByForFilters(get().sortBy, next),
      lastAppliedAt: Date.now(),
      currentPage: 1,
      scrollToIndex: null,
      highlightSpaceId: null,
    })
  },

  setMapCenter: (v) => set({ mapCenter: v }),
  setLastBounds: (v) => set({ lastBounds: v }),

  // ── UI actions ───────────────────────────────────────────────────────────

  toggleFiltersHidden: () => set({ filtersHidden: !get().filtersHidden }),
  setPage: (page) => set({ currentPage: page, previewedSpace: null }),
  setSortBy: (sortBy) => {
    const nextSortBy = coerceSortByForFilters(sortBy, get().filters)
    if (nextSortBy === get().sortBy) return
    set({
      sortBy: nextSortBy,
      currentPage: 1,
      lastAppliedAt: Date.now(),
      scrollToIndex: null,
      highlightSpaceId: null,
      autoFilterBanner: null,
      previewedSpace: null,
    })
  },

  // ── Marker / scroll ──────────────────────────────────────────────────────

  onMarkerClick: ({ spaceId, index }) => {
    const { pageStartIndex } = get()
    const absoluteIndex = pageStartIndex + index
    set({ scrollToIndex: absoluteIndex, highlightSpaceId: spaceId })
    window.setTimeout(() => {
      if (get().highlightSpaceId === spaceId) set({ highlightSpaceId: null })
    }, 10000)
  },

  onSuggestedMarkerClick: ({ spaceId }) => {
    set({ highlightSpaceId: spaceId })
    window.setTimeout(() => {
      if (get().highlightSpaceId === spaceId) set({ highlightSpaceId: null })
    }, 10000)
  },

  setHighlightSpaceId: (id) => set({ highlightSpaceId: id }),
  setHoveredSpaceId: (id) => set({ hoveredSpaceId: id }),
  setScrollToIndex: (index) => set({ scrollToIndex: index }),

  // ── Page items for map ───────────────────────────────────────────────────

  setPageItems: (items) => set({ pageItems: items }),
  setPageStartIndex: (index) => set({ pageStartIndex: index }),
  setSuggestedSpaces: (spaces) => set({ suggestedSpaces: spaces }),
  setVisibleRange: (range) => set({ visibleRange: range }),

  // ── Ghost venue ──────────────────────────────────────────────────────────

  setIsCreatingGhostVenue: (v) => set({ isCreatingGhostVenue: v }),

  // ── Event list dialog ────────────────────────────────────────────────────

  setSpaceBeingAddedToEvent: (v) => {
    set({ spaceBeingAddedToEvent: v })
  },

  // ── Hydrate from URL ─────────────────────────────────────────────────────

  hydrate: (filters, page, activeVenueGroupId, sortBy = DEFAULT_SPACE_SORT_BY) => {
    const prev = get().activeVenueGroupId
    const cityChanged = prev !== undefined && prev !== activeVenueGroupId
    set({
      filters,
      currentPage: page,
      sortBy: coerceSortByForFilters(sortBy, filters),
      lastAppliedAt: filters ? Date.now() : 0,
      searchAsMapMoves: false,
      mapCenter: null,
      lastBounds: null,
      activeVenueGroupId,
      // Only clear cached pins when switching between cities to prevent stale data
      ...(cityChanged ? { pageItems: [], suggestedSpaces: [], secondaryMapSpaces: [] } : {}),
    })
  },

  // ── Auto-filter banner ───────────────────────────────────────────────────

  setAutoFilterBanner: (banner) => set({ autoFilterBanner: banner }),

  // ── Label mappings ───────────────────────────────────────────────────────

  setLabelMappings: (mappings) => {
    const { filters } = get()
    if (!filters) return
    set({ filters: { ...filters, ...mappings } })
  },

  // ── Secondary map spaces ────────────────────────────────────────────────

  setSecondaryMapSpaces: (spaces) => set({ secondaryMapSpaces: spaces }),
  setPreviewedSpace: (space) => set({ previewedSpace: space }),
  onSecondaryMarkerClick: ({ spaceId }) => {
    set({ highlightSpaceId: spaceId })
    window.setTimeout(() => {
      if (get().highlightSpaceId === spaceId) set({ highlightSpaceId: null })
    }, 10000)
  },

  // ── Map zoom ────────────────────────────────────────────────────────────
  setMapZoom: (zoom) => set({ mapZoom: zoom }),
  setSecondaryMapLoading: (loading) => set({ secondaryMapLoading: loading }),
}))
